import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import _, { isEmpty } from 'lodash';
import moment from 'moment';
import { RootState } from 'store';

import { autoResults } from 'actions/autos';
import { PROMO } from 'components/AutoFilters';
import AutoHeader from 'components/AutoHeader';
import DataCardAuto from 'components/Cards/DataCardAuto';
import HeaderSearchDesktop from 'components/HeaderSearchDesktop';
import { Desktop, Mobile } from 'components/Responsive';
import TopButton from 'components/TopButton';
import AutosFilter from 'containers/AutosFilters';
import AutosSearchEdit from 'containers/AutosSearchEdit';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { Airport } from 'models/airport';
import { VehicleOption } from 'models/auto/vehicle';
import { VehicleFilterResponse } from 'models/auto/vehicleFilter';
import { VehicleResultsFilters } from 'models/auto/vehicleResults';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { City } from 'models/city';
import AutoDetails from 'screens/containers/AutoDetails';
import { amplitudelogEvent, SelectViewFiltersEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, htmlT } from 'utils/generic';

import DesktopSearchBar, { SearchSwitchEnum } from '../DesktopSearchBar';
import FilterTags from './components/FilterTags';
import FloatingFilter from './components/FloatingFilter';
import CardLoader from './components/LoadingCard';
import { mapAutoResultsSummary } from './mapper';
import { Container, Body, Cards, NotFound, OneWayIcon, Wrapper, LeftContainer } from './styles';

interface Props {
  pickUpPoint?: Airport | City | VehiclePlace;
  dropOffPoint?: Airport | City | VehiclePlace;
  pickUpDate?: Date;
  dropOffDate?: Date;
  isLoading: boolean;
  isLoadingResults: boolean;
  autosResult: VehicleOption[];
  onRequest: () => void;
  availableFilters?: VehicleFilterResponse;
  traveler?: TravelArrangerUsersAllowed;
}

const AutosList = ({
  autosResult,
  isLoading,
  isLoadingResults,
  pickUpPoint,
  dropOffPoint,
  pickUpDate,
  dropOffDate,
  onRequest,
  availableFilters,
  traveler,
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const filters = useSelector((state: RootState) => state.autos.filters);
  const user = useUserAccount();

  const [showList, setShowList] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAuto, setSelectedAuto] = useState<VehicleOption | undefined>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filterSearching, setFilterSearching] = useState(false);
  const [list, setList] = useState<VehicleOption[]>([]);
  const [showEdit, setShowEdit] = useState(false);
  const [forcedPage, setForcedPage] = useState(0);
  const { diffDropOff, search } = useSelector((state: RootState) => state.autos);

  const amplitudeViewFiltersEvent: SelectViewFiltersEvent = useMemo(
    () => ({
      VIEW_TYPE: '',
      TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
      TRAVEL_FROM: pickUpPoint?.code ?? '',
      TRAVEL_TO: dropOffPoint?.code ?? '',
      TRAVEL_DESTINATION_TYPE:
        search?.distanceRestriction && search?.distanceRestriction === 'Domestic' ? 'NATIONAL' : 'INTERNATIONAL',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), pickUpDate).toString(),
      TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
      TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(pickUpDate, dropOffDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: '',
    }),
    [dropOffDate, dropOffPoint?.code, pickUpDate, pickUpPoint?.code, search?.distanceRestriction],
  );

  const scrollTop = () => window.scrollTo({ top: 0 });

  const handleClickBack = () => history.goBack();
  const handleFilterClick = () => {
    amplitudeViewFiltersEvent.VIEW_TYPE = '';
    amplitudelogEvent(amplitudeEvents.SELECT_VIEW_FILTERS, amplitudeViewFiltersEvent, user);

    history.push('/autos/filters');
  };
  const handleFilterChange = async (filters: VehicleResultsFilters) => {
    setFilterSearching(true);
    setTimeout(scrollTop);
    await dispatch(autoResults(filters));
    setForcedPage(0);
    setFilterSearching(false);
    history.replace('/autos');
  };
  const handleEditClick = () => {
    setShowEdit(true);
    history.push('/autos/edit');
  };

  const handleScroll = useCallback(
    (e: Event) => {
      if (e.target !== document) return;
      if (isLoading || filterSearching || isLoadingResults) return;
      const hasReachedBottom = document.body.scrollHeight - window.scrollY <= document.body.clientHeight + 700;
      if (hasReachedBottom) onRequest();
    },
    [filterSearching, isLoading, isLoadingResults, onRequest],
  );

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    history.listen((location) => {
      if (location.pathname === '/autos') {
        setShowEdit(false);
        setShowList(true);
        setShowDetails(false);
        setShowFilters(false);
      }
      if (location.pathname.includes('/autos/details')) {
        setShowEdit(false);
        setShowDetails(true);
        setShowFilters(false);
        setShowList(!isMobile);
      }
      if (location.pathname === '/autos/filters') {
        setShowEdit(false);
        setShowDetails(false);
        setShowFilters(true);
      }
      if (location.pathname === '/autos/edit') {
        setShowEdit(true);
      }
    });
  }, [history, isMobile]);

  useEffect(() => {
    if (autosResult.length > 0) setList(mapAutoResultsSummary(autosResult));
    else setList([]);
  }, [autosResult]);

  useEffect(() => {
    if (showList && forcedPage === 0) {
      const qtyList = list.filter((c) => c.company?.name !== PROMO).length;
      if (qtyList < 9) {
        onRequest();
        setForcedPage(forcedPage + 1);
      }
    }
  }, [showList, list, onRequest, forcedPage]);

  const handleClickCard = useCallback(
    (id?: string) => {
      if (id) {
        history.push(`/autos/details/${id}`);
        const auto = autosResult.find((a) => a.optionId === id);
        setSelectedAuto(auto);
        const idx = autosResult.findIndex((a) => a.optionId === id);
        setSelectedIndex(idx);
      }
    },
    [autosResult, history],
  );

  const hasFilters = useCallback(() => {
    if (isLoading) return false;
    if (filterSearching) return false;
    if (typeof filters === 'undefined' || isEmpty(filters)) return false;
    const clearFilters = Object.entries(filters).filter(([k, v]) => {
      if (k !== 'orderCriteria' && k !== 'orderDesc') {
        return _.isArray(v) ? (v.length > 0 ? true : false) : _.isString(v) ? v !== '' : !!v;
      }
      return false;
    });
    return clearFilters.length > 0;
  }, [filterSearching, filters, isLoading]);

  const renderLoadingContent = () => (
    <Cards className="loading">
      {Number(3)
        .toArray()
        .map((number) => (
          <CardLoader
            key={number}
            col={{
              sm: 6,
              md: 6,
              lg: 6,
              xl: 6,
            }}
          />
        ))}
    </Cards>
  );

  const renderCards = () => {
    if (!isLoading && !isLoadingResults && !filterSearching && list.length === 0) {
      return (
        <>
          <Mobile>
            <FilterTags onChange={handleFilterChange} />
          </Mobile>
          <NotFound>
            <NotFound.Icon />
            {t('autosList.notFound')}
          </NotFound>
        </>
      );
    }

    return (
      <>
        {showList && (
          <Mobile>
            <FilterTags onChange={handleFilterChange} />
          </Mobile>
        )}
        <Cards className={hasFilters() ? 'filters list' : 'list'}>
          {!isLoading &&
            !filterSearching &&
            list
              .filter((c) => c.company?.name?.toUpperCase() !== PROMO)
              .map((data, index) => (
                <DataCardAuto
                  key={index}
                  data={data}
                  onClick={handleClickCard}
                  col={{
                    sm: 6,
                    md: 6,
                    lg: 6,
                    xl: 6,
                  }}
                />
              ))}
          {(isLoading || isLoadingResults || filterSearching) &&
            Number(3)
              .toArray()
              .map((number) => (
                <CardLoader
                  key={number}
                  col={{
                    sm: 6,
                    md: 6,
                    lg: 6,
                    xl: 6,
                  }}
                />
              ))}
        </Cards>
      </>
    );
  };

  const headerText: string = useMemo(
    () =>
      pickUpPoint?.related_city.includes(pickUpPoint?.country)
        ? pickUpPoint?.related_city
        : `${pickUpPoint?.related_city}, ${pickUpPoint?.country}`,
    [pickUpPoint],
  );

  const headerTitle = useMemo(
    () => (
      <>
        {t('autosList.desktopTitle')} {pickUpPoint?.related_city}
        {diffDropOff && (
          <>
            <OneWayIcon />
            {dropOffPoint?.related_city}
          </>
        )}
      </>
    ),
    [diffDropOff, dropOffPoint, pickUpPoint, t],
  );

  const subtitle = useMemo(() => {
    const fromDate = moment(pickUpDate).format('ddd[,] DD MMM');
    const toDate = moment(dropOffDate).format('ddd[,] DD MMM');
    const text = traveler ? (
      <>
        {fromDate} - {toDate}
        {''} {htmlT(t, 'autosSearch.toTraveler', { user: `${traveler.name} ${traveler.lastName}` })}
      </>
    ) : (
      <>
        {fromDate} - {toDate}
      </>
    );
    return text;
  }, [dropOffDate, pickUpDate, t, traveler]);

  const days = useMemo(() => moment(dropOffDate).diff(moment(pickUpDate), 'days'), [dropOffDate, pickUpDate]);

  return (
    <>
      {!isLoading && !filterSearching && <TopButton />}
      <Mobile>
        <AutoHeader
          info={{
            pickUpPoint: headerText,
            pickUpDate,
            dropOffDate,
          }}
          loading={isLoading}
          onEdit={handleEditClick}
          goBack={() => history.replace('/booking/autos')}
        />
      </Mobile>

      <Desktop>
        <DesktopSearchBar activeTab={SearchSwitchEnum.Autos} isLoading={isLoading || filterSearching} />
      </Desktop>
      <HeaderSearchDesktop title={headerTitle} subtitle={subtitle} hasFilters={hasFilters()} hasMap={false} />
      {!isLoading && !filterSearching && (
        <Desktop>
          <FilterTags onChange={handleFilterChange} />
        </Desktop>
      )}
      <Container>
        <LeftContainer>
          {(isLoading || filterSearching || (list.length === 0 && hasFilters()) || list.length > 0) && (
            <AutosFilter
              show={showFilters}
              onClose={handleClickBack}
              onChange={handleFilterChange}
              availableFilters={availableFilters}
              days={days}
              isLoading={isLoading || filterSearching}
              pickUpDate={pickUpDate}
              dropOffDate={dropOffDate}
              pickUpPoint={pickUpPoint}
              dropOffPoint={dropOffPoint}
            />
          )}
        </LeftContainer>
        {isLoading || filterSearching ? (
          <Wrapper>{renderLoadingContent()}</Wrapper>
        ) : (
          <Wrapper>
            <Body>
              <div>
                <Mobile>
                  {!isLoading && !filterSearching && !showFilters && !showDetails && (
                    <FloatingFilter
                      showMap={false}
                      hasMap={false}
                      onFilterClick={handleFilterClick}
                      onMapClick={() => {}}
                    />
                  )}
                </Mobile>
                {renderCards()}
              </div>
            </Body>
          </Wrapper>
        )}
      </Container>
      {selectedAuto && (
        <AutoDetails
          show={showDetails}
          auto={selectedAuto}
          onClose={handleClickBack}
          onSelect={handleClickCard}
          traveler={traveler}
          index={selectedIndex}
        />
      )}
      <Mobile>
        <AutosSearchEdit show={showEdit} onClose={handleClickBack} />
      </Mobile>
    </>
  );
};

export default AutosList;
